import type {StockDevelopmentEntry, StockDevelopmentResponse} from '@domain/schedule'
import {ApiClient} from '@hconnect/apiclient'
import {useUpmPlantContext} from '@hconnect/upm'
import {useQuery} from '@tanstack/react-query'
import {Moment} from 'moment-timezone'
import {useCallback} from 'react'

import {useStoredMaterialIdsFromHistoryQuery} from './useStoredMaterialIdsFromHistoryQuery'

type MomentRange = [start: Moment, end: Moment]

// We need an additional hour of stock levels data to display material orders correctly
const STOCK_DEVELOPMENT_END_HOURS_PADDING = 2

const STOCK_DEVELOPMENT_QUERY_KEY = 'stockDevelopment'

interface MaterialsStockLevelsQueryParams {
  planRange: MomentRange
  pointAtTime: Moment
  scheduleId?: number
}

export const useMaterialsStockLevelsQuery = ({
  planRange,
  pointAtTime,
  scheduleId
}: MaterialsStockLevelsQueryParams) => {
  const {plantId: plantCode} = useUpmPlantContext()
  // we do expect here input to be as string
  const beginningOfHour = pointAtTime.toISOString()
  const [planStart, planEnd] = planRange
  // BE doesn't support retrieving historical material stock levels, plantEnd should be > now
  const {data: materialIds} = useStoredMaterialIdsFromHistoryQuery({
    timeFrame: [planStart, planEnd]
  })

  const from = planStart.startOf('hour').toISOString()
  // We need an additional hour of stock levels data to display material orders correctly ( the same as in the useStockDevelopmentQuery )
  const to = planEnd
    .clone()
    .add(STOCK_DEVELOPMENT_END_HOURS_PADDING, 'hour')
    .startOf('hour')
    .toISOString()

  const selectStockDevelopment = useCallback(
    (stockDevelopment: StockDevelopmentResponse | undefined) => {
      if (!stockDevelopment) {
        return undefined
      }

      const findValueAtRequestedTime = (
        materialId: string,
        {actual, forecast}: StockDevelopmentEntry
      ) => {
        // sensitive to the ISO date string formatting
        const combinedStockLevels = actual.concat(forecast)
        const entry = combinedStockLevels.find(({datetime}) => datetime === beginningOfHour)
        if (!entry) {
          throw new Error(
            `BE does not return valid data. Error finding datetimeValue ${beginningOfHour} at materialId ${materialId}`
          )
        }
        return entry.value
      }

      // return object keyed by materialId and value containing value at the beginningOfHour ( beginning of pointAtTime )
      return Object.fromEntries(
        Object.entries(stockDevelopment).map(([materialId, stockDevelopmentEntry]) => [
          materialId,
          findValueAtRequestedTime(materialId, stockDevelopmentEntry)
        ])
      )
    },
    [beginningOfHour]
  )

  return useQuery<
    StockDevelopmentResponse | undefined,
    unknown,
    Record<string, number> | undefined
  >({
    queryKey: [
      STOCK_DEVELOPMENT_QUERY_KEY,
      {plantCode, materialIds: materialIds ?? [], scheduleId, from, to}
    ],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<StockDevelopmentResponse>(
        `plants/${plantCode}/schedules/kpi/stock-development`,
        {
          params: {
            from,
            to,
            materialIds: (materialIds ?? []).join(','),
            scheduleId
          },
          signal
        }
      )
      return response.data
    },
    enabled: Boolean(pointAtTime && materialIds?.length),
    select: selectStockDevelopment,
    throwOnError: true
  })
}
